/**
 * BlockTextDirection - Tiptap extension for per-block text direction (RTL/LTR).
 *
 * Adds a global `dir` attribute to text-block nodes so each paragraph,
 * heading, blockquote, or list item can be laid out independently. The
 * default is `"auto"`, which lets the browser's bidi algorithm pick the
 * direction from the block's first strong character — so a Hebrew paragraph
 * followed by an English paragraph each render correctly with no user action.
 *
 * The toolbar exposes `setTextDirection`/`toggleTextDirection` so a user can
 * override the auto-detection for an individual block when needed.
 *
 * Why not the built-in `@tiptap/core` TextDirection extension? It is included
 * by default but is inert unless a single *global* direction is configured,
 * and it exposes no commands — so it can't drive per-paragraph toggling. This
 * extension uses a distinct name (`blockTextDirection`) to coexist with the
 * inert built-in. Attribute/command shape mirrors @tiptap/extension-text-align.
 */
import { Extension } from '@tiptap/core';

export type TextDirectionValue = 'ltr' | 'rtl' | 'auto';

export interface BlockTextDirectionOptions {
  /** Node types the `dir` attribute is added to. */
  types: string[];
  /** Directions accepted by the commands. */
  directions: TextDirectionValue[];
  /** Default direction applied to every block (auto = browser bidi detection). */
  defaultDirection: TextDirectionValue;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockTextDirection: {
      /** Set the text direction of the selected block(s). */
      setTextDirection: (direction: TextDirectionValue) => ReturnType;
      /** Reset the selected block(s) back to the default direction. */
      unsetTextDirection: () => ReturnType;
      /** Toggle a direction: applies it, or resets to default if already active. */
      toggleTextDirection: (direction: TextDirectionValue) => ReturnType;
    };
  }
}

export const BlockTextDirection = Extension.create<BlockTextDirectionOptions>({
  name: 'blockTextDirection',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'blockquote', 'listItem'],
      directions: ['ltr', 'rtl', 'auto'],
      defaultDirection: 'auto',
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          dir: {
            default: this.options.defaultDirection,
            parseHTML: (element) => {
              const dir = element.getAttribute('dir');
              return this.options.directions.includes(dir as TextDirectionValue)
                ? dir
                : this.options.defaultDirection;
            },
            renderHTML: (attributes) => {
              if (!attributes.dir) {
                return {};
              }
              return { dir: attributes.dir };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextDirection:
        (direction) =>
        ({ commands }) => {
          if (!this.options.directions.includes(direction)) {
            return false;
          }
          return this.options.types
            .map((type) => commands.updateAttributes(type, { dir: direction }))
            .some((response) => response);
        },
      unsetTextDirection:
        () =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.resetAttributes(type, 'dir'))
            .some((response) => response),
      toggleTextDirection:
        (direction) =>
        ({ editor, commands }) => {
          if (!this.options.directions.includes(direction)) {
            return false;
          }
          if (editor.isActive({ dir: direction })) {
            return commands.setTextDirection(this.options.defaultDirection);
          }
          return commands.setTextDirection(direction);
        },
    };
  },
});
